import { useState, useRef } from "react";
import axios from "axios";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useAuth, API } from "../../App";
import { useI18n } from "../../i18n/I18nProvider";
import { extractError } from "../../lib/errors";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Progress } from "../../components/ui/progress";
import { Card, CardContent } from "../../components/ui/card";
import { TEXT_TONE } from "../../components/ui/status-badge";
import { getStatusTone } from "../../lib/statusTokens";
import {
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, ChevronDown, ChevronRight,
  FileText
} from "lucide-react";

// P1 (ADR-P1-001): color ya no vive aquí — sale de getStatusTone("audit", value) al
// renderizar. Se conserva solo el ícono, que no es parte del contrato de tono.
const RESULT_CONFIG = [
  { value: "not_assessed", labelKey: "audits.checklist.not_assessed", icon: null },
  { value: "compliant", labelKey: "audits.checklist.compliant", icon: CheckCircle2 },
  { value: "non_compliant", labelKey: "audits.checklist.non_compliant", icon: XCircle },
  { value: "observation", labelKey: "audits.checklist.observation", icon: AlertTriangle },
  { value: "not_applicable", labelKey: "audits.checklist.not_applicable", icon: null },
];

// S18/ADR-S18-002: virtualize only past this size. Most IT/ZF audits are
// well below it (ISO 27001 ~93, ZF sub-frameworks all <50); NORTIC A8 alone
// is 314, so this is where jank starts to matter.
const VIRTUALIZE_THRESHOLD = 80;

/**
 * NoteField — controlled with its own local state so a parent re-render
 * (e.g. onRefresh() triggered by another row's result change) never clobbers
 * in-progress, unsaved typing. Persists on blur only, and only if the value
 * actually changed from the last-known server value. `key={item.control_id}`
 * at the call site ensures it remounts (and resets its local state) when the
 * item it's bound to changes.
 */
const NoteField = ({ item, onSave, t }) => {
  const [val, setVal] = useState(item.notes || "");
  return (
    <Input
      className="w-full h-8 text-sm"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        if (val !== (item.notes || "")) {
          onSave(val);
        }
      }}
      placeholder={t("notes")}
    />
  );
};

/**
 * ChecklistRow — single checklist item card. Extracted (ADR-S18-002) so the
 * exact same markup renders in both the plain `.map()` path (small lists)
 * and the virtualized path (large lists like NORTIC A8 = 314 items), with
 * zero markup drift between the two.
 */
const ChecklistRow = ({ item, isExpanded, onToggleExpand, onUpdateResult, onSaveNote, auditLocale, t }) => {
  const resultCfg = RESULT_CONFIG.find((r) => r.value === item.result);
  const ResultIcon = resultCfg?.icon;
  const resultColor = TEXT_TONE[getStatusTone("audit", item.result)];
  const hasContext = item.description || item.implementation_guidance || (item.evidence_requirements?.length > 0);

  return (
    <Card className="border-border transition-colors">
      <CardContent className="p-3">
        {/* Collapsed row */}
        <div className="flex items-start gap-2">
          <button
            onClick={() => onToggleExpand(item.control_id)}
            className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={isExpanded ? t("audits.checklist.hide_context") : t("audits.checklist.show_context")}
          >
            {isExpanded
              ? <ChevronDown className="w-4 h-4" />
              : <ChevronRight className="w-4 h-4" />}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-muted-foreground shrink-0">
                {item.control_number}
              </span>
              <Badge variant="secondary" className="text-xs shrink-0">
                {item.framework}
              </Badge>
              {item._localized_lang && item._localized_lang !== (auditLocale || "en") && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">
                  {item._localized_lang.toUpperCase()}
                </Badge>
              )}
            </div>
            {/* Title — no truncate, text-balance */}
            <p className="text-sm mt-1 break-words text-balance leading-snug">
              {item.title}
            </p>
          </div>

          {/* Result selector */}
          <div className="flex items-center gap-1.5 shrink-0">
            {ResultIcon && <ResultIcon className={`w-4 h-4 ${resultColor}`} />}
            <Select
              value={item.result}
              onValueChange={(v) => onUpdateResult(item.control_id, v)}
            >
              <SelectTrigger className="w-36 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESULT_CONFIG.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {t(r.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Expanded context */}
        {isExpanded && (
          <div className="mt-3 pl-6 space-y-3 border-t border-border pt-3">
            {item.description && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  {t("description")}
                </p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.description}</p>
              </div>
            )}
            {item.category && (
              <div>
                <Badge variant="outline" className="text-xs">{item.category}</Badge>
              </div>
            )}
            {item.implementation_guidance && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  {t("audits.checklist.guidance_label")}
                </p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {item.implementation_guidance}
                </p>
              </div>
            )}
            {item.evidence_requirements?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  {t("audits.checklist.evidence_required_label")}
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
                  {item.evidence_requirements.map((req, i) => (
                    <li key={i}>{req}</li>
                  ))}
                </ul>
              </div>
            )}
            {!hasContext && (
              <p className="text-xs text-amber-600 italic">{t("audits.checklist.no_context")}</p>
            )}

            {/* Notes inline */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                {t("notes")}
              </p>
              <NoteField
                key={item.control_id}
                item={item}
                onSave={(val) => onSaveNote(item.control_id, val)}
                t={t}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * VirtualizedChecklist — @tanstack/react-virtual with dynamic measurement
 * (ADR-S18-002). Rows are expandable (variable height), so a fixed
 * estimateSize + `measureElement` is required: measureElement re-measures
 * a row's real DOM height after expand/collapse (via ResizeObserver) so
 * the scroll offset of items above it never jumps (risk R1).
 *
 * Own BOUNDED-HEIGHT scrollable container (F2, post-C3 smoke finding):
 * `flex-1 min-h-0` only clips when every ancestor up the chain has a
 * *definite* height. It doesn't here — AuditDetail's page root uses
 * `min-h-[calc(100vh-4rem)]` (a floor, not a cap), so the flex chain above
 * this component never resolves to a bounded size and `overflow-auto` was a
 * no-op: clientHeight == scrollHeight, so react-virtual's "visible window"
 * was the entire list and it mounted all 314 NORTIC A8 rows regardless of
 * scroll position (confirmed empirically: browser render check via
 * Playwright against a harness replicating the exact SidebarLayout →
 * AuditDetail → Tabs → TabsContent → AuditChecklistTab DOM/CSS chain).
 * `max-h-[70vh]` (floor `min-h-[320px]` for very short viewports) makes
 * this div bounded on its own, independent of ancestor layout, so it
 * becomes the real scroll-parent for react-virtual. Also intentionally
 * NOT the Radix ScrollArea: its Viewport is an internal node with no
 * exposed ref, so it can't be used as react-virtual's scroll element.
 */
const VirtualizedChecklist = ({ items, expandedId, onToggleExpand, onUpdateResult, onSaveNote, auditLocale, t }) => {
  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 88,
    overscan: 8,
  });

  return (
    <div ref={parentRef} className="max-h-[70vh] min-h-[320px] overflow-auto pr-4 pb-4">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          return (
            <div
              key={item.control_id}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="pb-2"
            >
              <ChecklistRow
                item={item}
                isExpanded={expandedId === item.control_id}
                onToggleExpand={onToggleExpand}
                onUpdateResult={onUpdateResult}
                onSaveNote={onSaveNote}
                auditLocale={auditLocale}
                t={t}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * AuditChecklistTab — ISO 19011 §6.4 Execution: full-page checklist.
 * - No fixed height (ScrollArea flex-1 — responsive to viewport).
 * - Items expandable: shows description, category, implementation_guidance, evidence_requirements.
 * - Filters: search, framework, result.
 * - Generate/Regenerate checklist preserves notes.
 * - S18/ADR-S18-002: filtered.length > VIRTUALIZE_THRESHOLD switches to a
 *   virtualized render (NORTIC A8 = 314 items); short lists keep the plain
 *   `.map()` path unchanged.
 */
const AuditChecklistTab = ({ audit, onRefresh }) => {
  const { token } = useAuth();
  const { t } = useI18n();

  const [expandedId, setExpandedId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [filterResult, setFilterResult] = useState("all");
  const [filterFramework, setFilterFramework] = useState("all");

  const checklist = audit.checklist || [];

  const generateChecklist = async () => {
    setGenerating(true);
    try {
      const locale = audit.locale || "en";
      const res = await axios.post(
        `${API}/audits/${audit.audit_id}/generate-checklist?locale=${locale}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || `Generated ${res.data.count} items`);
      onRefresh();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.checklist_failed")));
    } finally {
      setGenerating(false);
    }
  };

  const updateItem = async (controlId, result, notes) => {
    try {
      await axios.put(
        `${API}/audits/${audit.audit_id}/checklist/${controlId}`,
        { control_id: controlId, result, notes, evidence_ids: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onRefresh();
    } catch (e) {
      toast.error(extractError(e, t("audits.toast.item_failed")));
    }
  };

  const handleToggleExpand = (controlId) => {
    setExpandedId((prev) => (prev === controlId ? null : controlId));
  };
  const handleUpdateResult = (controlId, value) => {
    const item = checklist.find((it) => it.control_id === controlId);
    updateItem(controlId, value, item?.notes);
  };
  const handleSaveNote = (controlId, value) => {
    const item = checklist.find((it) => it.control_id === controlId);
    updateItem(controlId, item?.result, value);
  };

  // Unique frameworks for filter
  const frameworks = [...new Set(checklist.map((it) => it.framework).filter(Boolean))];

  const filtered = checklist.filter((it) => {
    const matchSearch =
      !search ||
      it.title?.toLowerCase().includes(search.toLowerCase()) ||
      it.control_number?.toLowerCase().includes(search.toLowerCase());
    const matchResult = filterResult === "all" || it.result === filterResult;
    const matchFw = filterFramework === "all" || it.framework === filterFramework;
    return matchSearch && matchResult && matchFw;
  });

  const shouldVirtualize = filtered.length > VIRTUALIZE_THRESHOLD;

  // Summary bar
  const total = checklist.length;
  const compliant = checklist.filter((i) => i.result === "compliant").length;
  const assessed = checklist.filter((i) => i.result !== "not_assessed").length;
  const rate = assessed > 0 ? Math.round((compliant / assessed) * 100) : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={generateChecklist}
          disabled={generating}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`} />
          {generating ? t("audits.generating") : t("audits.checklist.regenerate_btn")}
        </Button>
        {audit.locale && (
          <Badge variant="secondary" className="text-xs">
            {t("audits.checklist.locale_badge")}: {audit.locale.toUpperCase()}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {t("audits.checklist_header").replace("{count}", total)}
        </span>
      </div>

      {/* Summary row */}
      {total > 0 && (
        <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 text-sm">
          <span className="text-green-500 font-semibold">{compliant} {t("audits.summary.compliant")}</span>
          <span className="text-muted-foreground">|</span>
          <span>{rate}% {t("audits.summary.compliance_rate")}</span>
          <Progress value={rate} className="h-1.5 flex-1" />
        </div>
      )}

      {/* Filters */}
      {total > 0 && (
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={t("audits.checklist.search_ph")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm w-48"
          />
          <Select value={filterResult} onValueChange={setFilterResult}>
            <SelectTrigger className="h-8 text-xs w-40">
              <SelectValue placeholder={t("audits.checklist.filter_all")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("audits.checklist.filter_all")}</SelectItem>
              {RESULT_CONFIG.map((r) => (
                <SelectItem key={r.value} value={r.value}>{t(r.labelKey)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {frameworks.length > 1 && (
            <Select value={filterFramework} onValueChange={setFilterFramework}>
              <SelectTrigger className="h-8 text-xs w-40">
                <SelectValue placeholder={t("audits.checklist.filter_framework")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("audits.checklist.filter_framework")}</SelectItem>
                {frameworks.map((fw) => (
                  <SelectItem key={fw} value={fw}>{fw}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Checklist */}
      {checklist.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <FileText className="w-10 h-10 text-muted-foreground" />
          <p className="text-muted-foreground">{t("audits.generating")}</p>
          <Button size="sm" onClick={generateChecklist} disabled={generating}>
            {generating ? t("audits.generating") : t("audits.generate_checklist")}
          </Button>
        </div>
      ) : shouldVirtualize ? (
        <VirtualizedChecklist
          items={filtered}
          expandedId={expandedId}
          onToggleExpand={handleToggleExpand}
          onUpdateResult={handleUpdateResult}
          onSaveNote={handleSaveNote}
          auditLocale={audit.locale}
          t={t}
        />
      ) : (
        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-2 pr-4 pb-4">
            {filtered.map((item) => (
              <ChecklistRow
                key={item.control_id}
                item={item}
                isExpanded={expandedId === item.control_id}
                onToggleExpand={handleToggleExpand}
                onUpdateResult={handleUpdateResult}
                onSaveNote={handleSaveNote}
                auditLocale={audit.locale}
                t={t}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

export default AuditChecklistTab;
